import { useState, useEffect } from 'react'
import { STANDARD_CONDITIONS, type InventoryCard, type StandardCondition } from '@/types/inventory'

export type InventorySortOption = 'name-asc' | 'name-desc' | 'set-asc' | 'set-desc' | 'price-asc' | 'price-desc' | 'date-newest' | 'date-oldest'

interface InventoryFiltersProps {
  cards: InventoryCard[]
  onFilteredChange: (filterFunction: (card: InventoryCard) => boolean) => void
  onSortChange: (sortFunction: (a: InventoryCard, b: InventoryCard) => number) => void
}

export function InventoryFilters({ cards, onFilteredChange, onSortChange }: InventoryFiltersProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<InventorySortOption>('date-newest')
  const [selectedConditions, setSelectedConditions] = useState<Set<string>>(new Set())

  const recreateFilterFunction = (search: string, conditions: Set<string>) => {
    const filterFunction = (eachCard: InventoryCard): boolean => {
      if (search.trim()) {
        const query = search.toLowerCase().trim()
        const cardName = eachCard.card?.name?.toLowerCase() ?? ''
        const setName = eachCard.card?.set?.name?.toLowerCase() ?? ''
        const cardId = eachCard.cardId.toLowerCase()
        if (!cardName.includes(query) && !setName.includes(query) && !cardId.includes(query)) {
          return false
        }
      }

      if (conditions.size > 0) {
        const cond = eachCard.condition ?? ''
        if (!conditions.has(cond)) {
          return false
        }
      }

      return true
    }

    onFilteredChange(filterFunction)
  }

  const recreateSortFunction = (sort: InventorySortOption) => {
    onSortChange((a: InventoryCard, b: InventoryCard) => {
      switch (sort) {
        case 'name-asc':
          return (a.card?.name ?? a.cardId).localeCompare(b.card?.name ?? b.cardId)
        case 'name-desc':
          return (b.card?.name ?? b.cardId).localeCompare(a.card?.name ?? a.cardId)
        case 'set-asc':
          return (a.card?.set?.name ?? '').localeCompare(b.card?.set?.name ?? '')
        case 'set-desc':
          return (b.card?.set?.name ?? '').localeCompare(a.card?.set?.name ?? '')
        case 'price-asc':
          return (a.priceWhenAdded ?? 0) - (b.priceWhenAdded ?? 0)
        case 'price-desc':
          return (b.priceWhenAdded ?? 0) - (a.priceWhenAdded ?? 0)
        case 'date-newest':
          return new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime()
        case 'date-oldest':
          return new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime()
        default:
          return 0
      }
    })
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    recreateFilterFunction(value, selectedConditions)
  }

  const handleSortChange = (value: InventorySortOption) => {
    setSortBy(value)
    recreateSortFunction(value)
  }

  const handleConditionToggle = (condition: string) => {
    const newConditions = new Set(selectedConditions)
    if (newConditions.has(condition)) {
      newConditions.delete(condition)
    } else {
      newConditions.add(condition)
    }
    setSelectedConditions(newConditions)
    recreateFilterFunction(searchQuery, newConditions)
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setSortBy('date-newest')
    setSelectedConditions(new Set())
    recreateFilterFunction('', new Set())
  }

  useEffect(() => {
    recreateFilterFunction(searchQuery, selectedConditions)
    recreateSortFunction(sortBy)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const availableConditions = Array.from(new Set(cards.map((card) => card.condition).filter((c): c is StandardCondition => c != null))).sort()

  const hasActiveFilters = searchQuery.trim() !== '' || selectedConditions.size > 0

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4 space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <div className="relative">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by card name, set, or ID..."
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
        </div>

        <div className="sm:w-48">
          <select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value as InventorySortOption)}
            className="w-full py-2 px-3 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          >
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="set-asc">Set Name (A-Z)</option>
            <option value="set-desc">Set Name (Z-A)</option>
            <option value="price-asc">Price (Low-High)</option>
            <option value="price-desc">Price (High-Low)</option>
            <option value="date-newest">Date Added (Newest)</option>
            <option value="date-oldest">Date Added (Oldest)</option>
          </select>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Filter by Condition:</label>
          {hasActiveFilters && (
            <button onClick={handleClearFilters} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
              Clear Filters
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {STANDARD_CONDITIONS.map((condition) => {
            const isSelected = selectedConditions.has(condition)
            const count = cards.filter((card) => card.condition === condition).length

            return (
              <button
                key={condition}
                onClick={() => handleConditionToggle(condition)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  isSelected ? 'bg-blue-800 text-white hover:bg-blue-900' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {condition} ({count})
              </button>
            )
          })}
          {availableConditions
            .filter((cond) => !STANDARD_CONDITIONS.includes(cond))
            .map((condition) => {
              const isSelected = selectedConditions.has(condition)
              const count = cards.filter((card) => card.condition === condition).length

              return (
                <button
                  key={condition}
                  onClick={() => handleConditionToggle(condition)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    isSelected ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {condition} ({count})
                </button>
              )
            })}
        </div>
      </div>
    </div>
  )
}
